import type { TargetTier } from './policy';
export const TARGET_LONG_EDGE_BY_TIER: Record<TargetTier, number> = {
  '1k': 1024,
  '2k': 2048,
  '4k': 4096,
  '8k': 8192,
  '16k': 16384,
};
export const UPSCALER_MAX_LONG_EDGE = 16384;
// Native SeedVR target presets are 16:9 resolutions; P-image accepts integer MP.
export const UPSCALER_TARGET_PIXELS_BY_TIER: Record<TargetTier, number> = {
  '1k': 1920 * 1080,
  '2k': 2560 * 1440,
  '4k': 3840 * 2160,
  '8k': 33_000_000,
  '16k': 128_000_000,
};

// ponytail: measured GPT 1K estimate, not returned metadata; replace when the provider exposes dimensions.
export const UPSCALER_GPT_ESTIMATED_PIXELS = 1_572_864;
export const UPSCALER_P_IMAGE_MAX_FACTOR = 8;
export const UPSCALER_MAX_PIXELS = 128_000_000;
export const UPSCALER_PRINT_DPI = 300;
export const UPSCALER_CUSTOM_SIZE_FIELD = 'outputLongEdge';
export const UPSCALER_REALESRGAN_MAX_FACTOR = 10;
export const UPSCALER_REALESRGAN_MAX_INPUT_PIXELS = 2_096_704;

export function getUpscalerSizeTier(longEdge: number): TargetTier {
  if (!Number.isFinite(longEdge) || longEdge <= 0) {
    throw new Error('Output size must be positive.');
  }
  for (const [tier, limit] of Object.entries(TARGET_LONG_EDGE_BY_TIER)) {
    if (longEdge <= limit) return tier as TargetTier;
  }
  throw new Error('Output size exceeds 16K.');
}

export function getUpscalerRequestedLongEdge(input: Record<string, unknown>) {
  const custom = input[UPSCALER_CUSTOM_SIZE_FIELD];
  if (custom !== undefined && custom !== 0) {
    if (typeof custom !== 'number' || !Number.isSafeInteger(custom)) {
      throw new Error('Output size must be an integer pixel count.');
    }
    getUpscalerSizeTier(custom);
    return custom;
  }
  const tier = input.targetTier;
  if (
    typeof tier !== 'string' ||
    !Object.hasOwn(TARGET_LONG_EDGE_BY_TIER, tier)
  ) {
    throw new Error('Unknown output size tier.');
  }
  return TARGET_LONG_EDGE_BY_TIER[tier as TargetTier];
}

/** Real-ESRGAN only accepts a scale, so Target and Factor converge here. */
export function getUpscalerRealEsrganScale(
  input: Record<string, unknown>,
  source: { width: number; height: number }
) {
  assertDimensions(source);
  const requested = getUpscalerRequestedLongEdge(input);
  const tier = getUpscalerSizeTier(requested);
  const raw =
    !input[UPSCALER_CUSTOM_SIZE_FIELD] && (tier === '8k' || tier === '16k')
      ? Math.sqrt(
          UPSCALER_TARGET_PIXELS_BY_TIER[tier] / (source.width * source.height)
        )
      : requested / Math.max(source.width, source.height);
  return Math.max(1, Math.ceil(raw * 10) / 10);
}

export function getUpscalerRealEsrganMaxFactor(source: {
  width: number;
  height: number;
}) {
  assertDimensions(source);
  if (source.width * source.height > UPSCALER_REALESRGAN_MAX_INPUT_PIXELS) {
    return 0;
  }
  return Math.min(
    UPSCALER_REALESRGAN_MAX_FACTOR,
    Math.floor(
      (UPSCALER_MAX_LONG_EDGE / Math.max(source.width, source.height)) * 10
    ) / 10
  );
}

export function estimateUpscalerGptDimensions(
  image: { width: number; height: number },
  resolution: '1k' | '2k' | '4k' = '1k'
) {
  assertDimensions(image);
  let pixels = UPSCALER_GPT_ESTIMATED_PIXELS;
  if (resolution !== '1k') {
    // APIMart published 2K/4K ratio table; auto may still choose a different ratio.
    const sizes = [
      [1, 2048, 2048, 2880, 2880],
      [3 / 2, 2048, 1360, 3520, 2336],
      [4 / 3, 2048, 1536, 3312, 2480],
      [5 / 4, 2560, 2048, 3216, 2576],
      [16 / 9, 2048, 1152, 3840, 2160],
      [2, 2688, 1344, 3840, 1920],
      [3, 3072, 1024, 3840, 1280],
      [21 / 9, 2688, 1152, 3840, 1648],
    ];
    const ratio =
      Math.max(image.width, image.height) / Math.min(image.width, image.height);
    const match = sizes.reduce((best, entry) =>
      Math.abs(entry[0] - ratio) < Math.abs(best[0] - ratio) ? entry : best
    );
    pixels = resolution === '2k' ? match[1] * match[2] : match[3] * match[4];
  }
  const factor = Math.sqrt(pixels / (image.width * image.height));
  return {
    width: Math.max(1, Math.round(image.width * factor)),
    height: Math.max(1, Math.round(image.height * factor)),
  };
}

export function getUpscalerGptResolution(
  input: Record<string, unknown>
): '1k' | '2k' | '4k' {
  if (input.highQuality !== true) return '1k';
  const tier = getUpscalerSizeTier(getUpscalerRequestedLongEdge(input));
  return tier === '1k' || tier === '2k' ? tier : '4k';
}

export function getUpscalerSourceDimensions(
  input: Record<string, unknown>,
  original: { width: number; height: number }
) {
  return input.highQuality !== true && input.scene === 'general'
    ? original
    : estimateUpscalerGptDimensions(original, getUpscalerGptResolution(input));
}

function assertDimensions(image: { width: number; height: number }) {
  if (
    !Number.isSafeInteger(image.width) ||
    !Number.isSafeInteger(image.height) ||
    image.width <= 0 ||
    image.height <= 0
  ) {
    throw new Error('Image dimensions are unavailable.');
  }
}

export function calculateUpscalerOutputSize(
  input: Record<string, unknown>,
  source: { width: number; height: number; hasTransparentPixels?: boolean }
) {
  assertDimensions(source);
  const longEdge = getUpscalerRequestedLongEdge(input);
  let factor = Math.max(1, longEdge / Math.max(source.width, source.height));
  const tier = getUpscalerSizeTier(longEdge);
  const pImage = tier === '8k' || tier === '16k';
  if (input.highQuality === true && !pImage) {
    return {
      factor: 1,
      width: source.width,
      height: source.height,
      tier,
      targetMegapixels: Math.ceil((source.width * source.height) / 1_000_000),
      exceedsLimit:
        Math.max(source.width, source.height) > UPSCALER_MAX_LONG_EDGE ||
        source.width * source.height > UPSCALER_MAX_PIXELS,
    };
  }
  const custom = Boolean(input[UPSCALER_CUSTOM_SIZE_FIELD]);
  const transparent = source.hasTransparentPixels === true;
  if (!custom) {
    factor = transparent
      ? getUpscalerRealEsrganScale(input, source)
      : Math.sqrt(
          UPSCALER_TARGET_PIXELS_BY_TIER[tier] / (source.width * source.height)
        );
    if (!pImage) factor = Math.max(1, factor);
  }
  let width = Math.round(source.width * factor);
  let height = Math.round(source.height * factor);
  const targetMegapixels =
    pImage && !custom && !transparent
      ? UPSCALER_TARGET_PIXELS_BY_TIER[tier] / 1_000_000
      : Math.ceil((width * height) / 1_000_000);
  if (pImage && !custom && !transparent) {
    const targetFactor = Math.sqrt(
      (targetMegapixels * 1_000_000) / (source.width * source.height)
    );
    width = Math.floor(source.width * targetFactor);
    height = Math.floor(source.height * targetFactor);
  }
  return {
    factor,
    width,
    height,
    tier,
    targetMegapixels,
    exceedsLimit:
      ((custom || transparent || !pImage) &&
        Math.max(width, height) > UPSCALER_MAX_LONG_EDGE) ||
      (!custom &&
        transparent &&
        factor > getUpscalerRealEsrganMaxFactor(source)) ||
      (pImage && (custom || !transparent)
        ? targetMegapixels * 1_000_000 > UPSCALER_MAX_PIXELS ||
          (custom && factor > UPSCALER_P_IMAGE_MAX_FACTOR)
        : custom && factor > 10),
  };
}

/** Find the largest valid factor across both model ranges using the execution sizing rules. */
export function getUpscalerMaxFactor(
  original: { width: number; height: number },
  source: { width: number; height: number },
  input: Record<string, unknown> = {}
) {
  assertDimensions(original);
  let maximum = 0;
  for (const [start, end] of [
    [1, TARGET_LONG_EDGE_BY_TIER['4k']],
    [TARGET_LONG_EDGE_BY_TIER['4k'] + 1, UPSCALER_MAX_LONG_EDGE],
  ]) {
    let low = start;
    let high = end;
    while (low <= high) {
      const edge = Math.floor((low + high) / 2);
      if (
        calculateUpscalerOutputSize(
          { ...input, outputLongEdge: edge },
          input.highQuality === true
            ? getUpscalerSourceDimensions(
                { ...input, outputLongEdge: edge },
                original
              )
            : source
        ).exceedsLimit
      )
        high = edge - 1;
      else {
        maximum = Math.max(maximum, edge);
        low = edge + 1;
      }
    }
  }
  return (
    Math.floor((maximum / Math.max(original.width, original.height)) * 100) /
    100
  );
}

export type UpscalerSizeUnit = 'px' | 'mm' | 'cm' | 'in';
export function sizeToPixels(
  value: number,
  unit: UpscalerSizeUnit,
  ppi: number
) {
  return unit === 'px'
    ? value
    : (value * ppi) / (unit === 'cm' ? 2.54 : unit === 'mm' ? 25.4 : 1);
}
export function pixelsToSize(
  value: number,
  unit: UpscalerSizeUnit,
  ppi: number
) {
  return unit === 'px'
    ? value
    : (value / ppi) * (unit === 'cm' ? 2.54 : unit === 'mm' ? 25.4 : 1);
}

// Values are physical sizes, independent of locale and display rounding.
export const UPSCALER_PRINT_PRESETS = [
  ...[
    [9, 11],
    [11, 17],
    [18, 24],
    [18, 36],
    [24, 36],
  ].map(([width, height]) => ({
    id: `poster-${width}-${height}`,
    group: 'posters' as const,
    label: `${width} × ${height} in`,
    width,
    height,
    unit: 'in' as const,
  })),
  ...[
    [8, 6],
    [8, 8],
    [8, 11],
    [8, 12],
    [12, 8],
    [9, 12],
    [12, 12],
    [16, 12],
    [18, 12],
  ].map(([width, height]) => ({
    id: `photo-${width}-${height}`,
    group: 'photos' as const,
    label: `${width} × ${height} in`,
    width,
    height,
    unit: 'in' as const,
  })),
  ...[
    [148, 210],
    [210, 297],
    [297, 420],
    [420, 594],
    [594, 841],
    [841, 1189],
  ].map(([width, height], index) => ({
    id: `A${5 - index}`,
    group: 'paper' as const,
    label: `A${5 - index}`,
    width,
    height,
    unit: 'mm' as const,
  })),
];
